import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import {
  Divider,
  Grid,
  Paper,
  Stack,
  Typography,
  VoyageProgressInformation
} from '@esgian/esgianui';
import { ProfileLink } from '@components/Links';
import VesselOverview from '@components/Sections/ShipAnalytics/VesselProfile/SubSections/VesselOverviewSection/VesselOverview';
import moment from 'moment/moment';
import VesselSpeedHistogram from '@components/Sections/ShipAnalytics/VesselProfile/SubSections/VesselOverviewSection/VesselSpeedHistogram';
import OwnerOrOperatorHistory from '@components/Sections/ShipAnalytics/VesselProfile/SubSections/VesselOverviewSection/OwnerOrOperatorHistory';
import { useSegment } from '@hooks';
import { useQuery } from '@tanstack/react-query';
import { useParams, useSearchParams } from 'react-router-dom';
import { VesselsMap } from '@components/Maps';
import { TextWithTooltipIcon } from '@components';
import {
  getVesselDetailsByFilters,
  getVesselLatestPosition,
  getVesselNextPortCall,
  getVesselYearlySpeedHistogram
} from '@api';

import { VESSEL_SPEED_HISTOGRAM_TITLE } from '@constants/tooltips';

const OVERVIEW_MAP_FULLSCREEN_PARAM = 'overviewMapFullscreen';

function VesselOverviewSection({ portCallsQuery = {} }) {
  const params = useParams();
  const [searchParams] = useSearchParams();
  const isMapFullscreen = searchParams.get(OVERVIEW_MAP_FULLSCREEN_PARAM) === 'true';
  const { isRoRo } = useSegment();

  const vesselNextPortCallQuery = useQuery({
    queryKey: ['vesselNextPortCall', params.imo],
    enabled: !!params.imo,
    placeholderData: null,
    queryFn: ({ signal, queryKey }) => {
      return getVesselNextPortCall(signal, queryKey[1], 1.0)
        .then((result) => result)
        .catch(() => null);
    }
  });
  const vesselLatestPositionQuery = useQuery({
    queryKey: ['vesselLatestPosition', params.imo],
    enabled: !!params.imo,
    placeholderData: null,
    queryFn: ({ signal, queryKey }) => {
      return getVesselLatestPosition(queryKey[1], signal)
        .then((result) => result)
        .catch(() => null);
    }
  });

  const vesselDetailsByFiltersQuery = useQuery({
    queryKey: ['vesselDetailsByFilters', parseInt(params.imo)],
    enabled: !!params.imo,
    placeholderData: null,
    queryFn: ({ signal, queryKey }) => {
      return getVesselDetailsByFilters(
        {
          imos: [queryKey[1]]
        },
        signal
      )
        .then((result) => result.vesselInformation[0])
        .catch(() => null);
    }
  });
  const vesselYearlySpeedHistogramQuery = useQuery({
    queryKey: ['VesselYearlySpeedHistogram', parseInt(params.imo)],
    enabled: !!params.imo,
    placeholderData: null,
    queryFn: ({ signal, queryKey }) => {
      return getVesselYearlySpeedHistogram(queryKey[1], signal)
        .then((result) => result)
        .catch(() => null);
    }
  });

  const {
    portId: etaPortId,
    portName: etaPortName,
    portUnlocode,
    destination,
    eta,
    activePort
  } = vesselNextPortCallQuery.data || {};

  const { portId, portName, portCode, endDate, startDate } = portCallsQuery.data
    ? portCallsQuery.data[0]
    : {};

  const { operatorInformation, ownershipInformation, vessel } =
    vesselDetailsByFiltersQuery?.data || {};
  const { vesselName } = vessel || {};

  const renderedMap = useMemo(
    () => (
      <VesselsMap
        showSpeedLegend
        loading={vesselLatestPositionQuery.isFetching}
        height={isMapFullscreen ? '98vh' : '40vh'}
        zoom={1}
        projection={'mercator'}
        vessels={vesselLatestPositionQuery.data ? [vesselLatestPositionQuery.data] : []}
        isFullscreen={isMapFullscreen}
        allowFullscreen={true}
      />
    ),
    [vesselLatestPositionQuery.isFetching, vesselLatestPositionQuery.data, isMapFullscreen]
  );

  if (isMapFullscreen) {
    return renderedMap;
  }

  return (
    <>
      <Grid item xs={12}>
        <Paper>
          <Grid item xs={12} sx={{ p: 2 }} id={'vessel-journey'}>
            <VoyageProgressInformation
              atdPortLink={
                <ProfileLink linkVariant={'h6'} id={portId} name={portName} profile={'port'} />
              }
              etaPortLink={
                <ProfileLink
                  disabled={!activePort}
                  linkVariant={'h6'}
                  id={etaPortId}
                  name={etaPortName || destination}
                  profile={'port'}
                />
              }
              ataDate={startDate}
              atdPortCode={portCode}
              loading={portCallsQuery.isFetching || vesselNextPortCallQuery.isFetching}
              etaDate={eta}
              etaPortCode={portUnlocode}
              atdDate={endDate}
              id={'vessel'}
            />
          </Grid>
        </Paper>
      </Grid>
      <Grid item xs={6}>
        <VesselOverview
          vesselsDataByFilters={vesselDetailsByFiltersQuery.data}
          loading={vesselDetailsByFiltersQuery.isFetching}
          isRoRo={isRoRo}
        />
      </Grid>
      <Grid item xs={6}>
        {renderedMap}
      </Grid>
      <Grid item xs={12}>
        <Paper sx={{ p: 2 }}>
          <Stack spacing={2}>
            <Stack>
              <TextWithTooltipIcon
                label={'Speed Histogram'}
                tooltipText={VESSEL_SPEED_HISTOGRAM_TITLE}
                labelVariant={'h6'}
              />
              <Typography variant={'body2'} color={'text.secondary'}>
                Last 12 months vs {moment().year() - 1}
              </Typography>
            </Stack>
            <Divider />
            <VesselSpeedHistogram
              speedHistogramData={vesselYearlySpeedHistogramQuery.data}
              loading={vesselYearlySpeedHistogramQuery.isFetching}
              fileName={`${vesselName}-speed-histogram`}
            />
          </Stack>
        </Paper>
      </Grid>
      <Grid item xs={6}>
        <OwnerOrOperatorHistory
          ownerOrOperator={'Operator'}
          loading={vesselDetailsByFiltersQuery.isFetching}
          historyData={operatorInformation}
        />
      </Grid>
      {isRoRo && (
        <Grid item xs={6}>
          <OwnerOrOperatorHistory
            ownerOrOperator={'Owner'}
            loading={vesselDetailsByFiltersQuery.isFetching}
            historyData={ownershipInformation}
          />
        </Grid>
      )}
    </>
  );
}

VesselOverviewSection.propTypes = {
  portCallsQuery: PropTypes.object
};

export default VesselOverviewSection;
